import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, DialogTitle, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import upperFirst from "lodash/upperFirst";
import { Button } from "@financeable-com-au/financeable-ui";
import { ApplicationType } from "@/store/slices/types/applicationFormSlice";
import { useAccessAppComponent } from "@/hooks/useAccessAppComponent";
import { useFeatureFlags } from "@/utils/featureFlags";
import { applicationPurpose, ERRORS_MESSAGE, getApplicationTab, getStartApplicationUrl, ROUTES } from "../../constants";
import {
  saveValue,
  startApplication,
  applicationSelector,
  userDetailsSelector,
} from "../../store/slices/applicationFormSlice";
import { userPermissionsSelector, userSelector } from "../../store/slices/userSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";

export function StartApplicationDialog({ open, handleClose, startApplication, applicationTypeList, isDemoCommercialApplication }) {

  const filteredApplicationTypeList = applicationTypeList?.filter((item) => {
    const isCommercialType = item?.label?.toLowerCase() === ApplicationType.Commercial;
    return isDemoCommercialApplication ? isCommercialType : !isCommercialType;
  });

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>Select application type</DialogTitle>
      <List sx={{ pt: 0 }}>
        {filteredApplicationTypeList?.map((item) => {
          return (
            <ListItem key={item?.key}>
              <ListItemButton onClick={() => startApplication(item?.value, item?.key)}>
                <ListItemText primary={item?.label} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Dialog>
  );
}
export default function StartApplication() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDemoCommercialApplicationEnabled } = useFeatureFlags();
  const userDetails = useSelector(userSelector);
  const permissions = userDetails?.permissions || {};
  const { accessComponents } = useAccessAppComponent();
  const {
    application: { type },
  } = accessComponents;
  const isQuoteEnable = permissions?.application?.quote_create;
  const isApplicationEnable = permissions?.application?.create;
  const isOrganisationEnable = permissions?.org?.create;
  const canInviteUsers = permissions?.user?.invite;
  const isAdminUser = userDetails?.isAdmin;
  const { isNewApplication } = useSelector(userDetailsSelector);
  const { _id } = useSelector(applicationSelector);
  const [openDialog, setOpenDialog] = useState(false);
  let currentActiveTab = "";
  const pathName = location?.pathname;

  const { _id: userId } = useSelector(userSelector);

  if (pathName?.includes(getApplicationTab.quote)) {
    currentActiveTab = getApplicationTab.quote;
  } else if (pathName?.includes(getApplicationTab.calculator)) {
    currentActiveTab = getApplicationTab.calculator;
  } else if (pathName?.includes(getApplicationTab.organisation)) {
    currentActiveTab = getApplicationTab.organisation;
  } else if (pathName?.includes(getApplicationTab.user)) {
    currentActiveTab = getApplicationTab.user;
  } else if (pathName?.includes(getApplicationTab.invoice)) {
    currentActiveTab = getApplicationTab.invoice;
  } else {
    currentActiveTab = getApplicationTab.application;
  }

  const handle = {
    handleClose: () => {
      setOpenDialog(false);
    },
    startApplication: (applicationType, applicationKey) => {
      const url = getStartApplicationUrl[currentActiveTab];
      const apiUrl = `${url}/${applicationType}${applicationKey === "startCommercialApp" ? `?purpose=${applicationPurpose.loanPurposeDemo}` : ""}`;
      dispatch(startApplication({ url: apiUrl })).then((res) => {
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
    },
    createCalculatorApplication: () => {
      handle.startApplication(ApplicationType.Commercial);
    },
    createOrganisation: () => {
      navigate("/organisation/new");
    },
  };
  useEffect(() => {
    if (isNewApplication && _id) {
      dispatch(saveValue({ isNewApplication: false }));
      navigate(`/${currentActiveTab}/${_id}`);
      handle.handleClose();
    }
  }, [isNewApplication]);

  const buttonLabel = `New ${upperFirst(getApplicationTab[currentActiveTab])}`;

  return (
    <>
      {getApplicationTab[currentActiveTab] === getApplicationTab.application && isApplicationEnable && (
        <Button
          variant="primary"
          onClick={async () => {
            setOpenDialog(true);
          }}
          style={{ marginRight: "30px" }}
        >
          {buttonLabel}
        </Button>
      )}
      {getApplicationTab[currentActiveTab] === getApplicationTab.quote && isQuoteEnable && (
        <Button
          variant="primary"
          onClick={async () => {
            setOpenDialog(true);
          }}
          style={{ marginRight: "30px" }}
        >
          {buttonLabel}
        </Button>
      )}
      {getApplicationTab[currentActiveTab] === getApplicationTab.calculator && (
        <Button variant="primary" onClick={handle.createCalculatorApplication} style={{ marginRight: "30px" }}>
          {buttonLabel}
        </Button>
      )}
      {getApplicationTab[currentActiveTab] === getApplicationTab.organisation && isOrganisationEnable && (
        <Button variant="primary" onClick={handle.createOrganisation} style={{ marginRight: "30px" }}>
          {buttonLabel}
        </Button>
      )}
      {getApplicationTab[currentActiveTab] === getApplicationTab.user && canInviteUsers && (
        <Button variant="primary" onClick={() => navigate(`/invite/${userId}`)} style={{ marginRight: "30px" }}>
          Invite Users
        </Button>
      )}
      {isAdminUser && getApplicationTab[currentActiveTab] === getApplicationTab.invoice && (
        <Button variant="primary" onClick={() => navigate(ROUTES.ADMIN.INVOICE_CREATE)} style={{ marginRight: "30px" }}>
          {buttonLabel}
        </Button>
      )}
      <StartApplicationDialog
        open={openDialog}
        handleClose={handle.handleClose}
        startApplication={handle.startApplication}
        applicationTypeList={type[currentActiveTab]}
        isDemoCommercialApplication={isDemoCommercialApplicationEnabled()}
      />
    </>
  );
}
